import { Stack, Typography, Checkbox, FormControlLabel, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormContext, Controller } from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { AssetType } from 'hooks/useGetOptionStartInspection';
import { dropdownValueTypes } from 'const/option';
import { capitalizeFirstLetter } from 'utils/convertData';
import { InputSelect } from 'app/components/elements/InputSelect';
import { SelectChangeEvent } from '@mui/material/Select';
import { InputText } from 'app/components/elements/InputText';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AddPhoto } from '../elements/AddPhoto';
import { ButtonCustom } from '../elements/ButtonCustom';
import { useFilteredRoomContext } from 'hooks/useFilteredRoomContext';
import dayjs from 'dayjs';
import { DatePicker } from '../elements/DatePicker';
import { useParams } from 'react-router-dom';
import { compact } from 'lodash';
import { MaintenanceConfig } from 'app/pages/ValidationsPage/type'; // ← ADD
import { normalizeTriggerSubTypeId, useTriggerSubTypeStore } from 'store/triggerSubType';
import { useTriggerTypeStore } from 'store/triggerType';
import { DialogConfirm } from '../elements/DialogConfirm';
import { NumericInput } from '../elements/NumericInput';
import { entriesInclude } from 'utils/entries';
import { getIssuesConfig } from 'services/option';
import { DEFAULT_CRITICALITY_LEVELS, toCriticalitySelectOptions } from 'utils/issueCriticality';

export interface AssetTypeEntities {
  [key: number]: AssetType;
}
export const AssetEntryForm = () => {
  const { clientId } = useParams();
  const { systemDropdownValues, singleEntryField, listOption } = useFilteredRoomContext(clientId);

  const {
    control,
    watch,
    setValue,
    formState: { errors }
  } = useFormContext();
  const [assetTypeEntities, setAssetTypeEntities] = useState<AssetTypeEntities>({});
  useEffect(() => {
    const ids: number[] = [];
    const entities: AssetTypeEntities = {};
    listOption?.asset_types?.forEach((item) => {
      ids.push(item.id);
      entities[item.id] = item;
    });
    setAssetTypeEntities(entities);
  }, [listOption?.asset_types]);

  // Legacy integer criticality (1–5) — replaced by criticalityKey from Issues Config
  // const criticalityOptions = useMemo(
  //   () => [
  //     { label: '1', value: '1' },
  //     { label: '2', value: '2' },
  //     { label: '3', value: '3' },
  //     { label: '4', value: '4' },
  //     { label: '5', value: '5' }
  //   ],
  //   []
  // );
  const [criticalityKeyOptions, setCriticalityKeyOptions] = useState(
    toCriticalitySelectOptions(DEFAULT_CRITICALITY_LEVELS)
  );
  useEffect(() => {
    getIssuesConfig()
      .then((res) => {
        const levels = res?.config?.criticalityLevels;
        if (levels?.length) {
          setCriticalityKeyOptions(toCriticalitySelectOptions(levels));
        }
      })
      .catch(() => undefined);
  }, []);

  const assetIssueOptions = useMemo(() => {
    return (
      systemDropdownValues?.filter((item) =>
        item.options.some((opt) => opt.type === dropdownValueTypes.asset)
      ) || []
    );
  }, [systemDropdownValues]);

  const assetIssueField = useMemo(() => {
    return singleEntryField?.filter((item) =>
      entriesInclude(item.entries, dropdownValueTypes.asset)
    );
  }, [singleEntryField]);

  // --- dynamic asset entries grouping (scaffold) ---
  const watchedAssetType = watch('assetType');
  const assetDynamic = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const entities: Record<string, any[]> = {};
    const keys: string[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const entries: any[] = [];

    const typeId = watchedAssetType;
    const selectedType = assetTypeEntities[typeId];

    if (!selectedType?.assetEntries) return { entities, keys, entries };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    selectedType.assetEntries.forEach((item: any, index: number) => {
      const fieldDef = item.field;

      // Get current values or use defaults
      const currentValue = watch(`entries.${index}.value`);
      const currentDropdownValueId = watch(`entries.${index}.dropdownValueId`);

      // Use default values if no current value exists
      const value =
        currentValue !== undefined && currentValue !== ''
          ? currentValue
          : fieldDef.defaultValue || '';
      const dropdownValueId =
        currentDropdownValueId !== undefined && currentDropdownValueId !== null
          ? currentDropdownValueId
          : fieldDef.defaultDropdownValueId || null;
      const dropdownOtherValue = watch(`entries.${index}.dropdownOtherValue`) || null;

      entries.push({
        fieldId: fieldDef.id,
        value,
        dropdownValueId,
        dropdownOtherValue
      });

      const category = fieldDef.category || 'GENERAL';
      if (!entities[category]) entities[category] = [];
      entities[category].push({
        ...item,
        index,
        value
      });
    });
    setValue('entries', entries);
    return {
      entities,
      keys: Object.keys(entities),
      entries
    };
  }, [watchedAssetType, assetTypeEntities, setValue, watch]);
  const {
    fields: photoFields,
    append: appendPhoto,
    remove: removePhoto
  } = useFieldArray({
    control,
    name: 'photos'
  });

  const dropdownOptionMap = useMemo(() => {
    const map = new Map();
    systemDropdownValues?.forEach((item) => {
      map.set(item.id.toString(), item);
    });
    return map;
  }, [systemDropdownValues]);

  // State for OEM dropdown options
  // OEM Attributes - Dropdown options state
  const oemDropdownOptions = useMemo(() => {
    return (
      systemDropdownValues?.filter((item) =>
        item.options.some((opt) => opt.type === dropdownValueTypes.assetModel)
      ) || []
    );
  }, [systemDropdownValues]);

  // Get dropdown values for a specific dropdown option ID
  const getOemDropdownValues = useCallback(
    (dropdownId: number | undefined) => {
      if (!dropdownId) return [];
      const option = oemDropdownOptions.find((opt) => opt.id === dropdownId);
      return option?.dropdownValues || [];
    },
    [oemDropdownOptions]
  );

  // ─── Maintenance config state ─────────────────────────────────────────────
  const { triggerSubTypes: subTypeOptions } = useTriggerSubTypeStore();
  const { triggerTypes: triggerTypeOptions } = useTriggerTypeStore();
  const masterConfigsRef = useRef<MaintenanceConfig[]>([]);
  const shownIndicesRef = useRef<number[]>([]);
  const [masterConfigsLength, setMasterConfigsLength] = useState(0);
  const [modelEnabled, setModelEnabled] = useState(false);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(null);
  const setMasterConfigs = (configs: MaintenanceConfig[], enabled: boolean) => {
    masterConfigsRef.current = configs;
    setMasterConfigsLength(configs.length);
    setModelEnabled(enabled);
  };

  const watchedMaintenanceConfigs: MaintenanceConfig[] = watch('maintenanceConfigs') || [];
  const watchMaintenanceEnabled = watch('maintenanceEnabled');

  const hasAnyConfigs = masterConfigsLength > 0 && modelEnabled;

  const handleMaintenanceToggle = (checked: boolean) => {
    setValue('maintenanceEnabled', checked);
    if (!checked) {
      setValue('maintenanceConfigs', []);
      // intentionally NOT clearing shownIndicesRef — preserves user's removal state
    } else {
      // restore only what was shown before unchecking (respects prior removals)
      const indicesToShow =
        shownIndicesRef.current.length > 0
          ? shownIndicesRef.current
          : masterConfigsRef.current.map((_, i) => i);
      shownIndicesRef.current = indicesToShow;
      setValue(
        'maintenanceConfigs',
        indicesToShow.map((masterIdx) => ({ ...masterConfigsRef.current[masterIdx] }))
      );
    }
  };

  const handleRemoveMaintenanceConfig = (shownPosition: number) => {
    const currentEdits = [...watchedMaintenanceConfigs]; // snapshot edits
    const oldShownIndices = [...shownIndicesRef.current]; // snapshot order

    const newShownIndices = oldShownIndices.filter((_, i) => i !== shownPosition);
    shownIndicesRef.current = newShownIndices;

    const nextConfigs = newShownIndices.map((masterIdx) => {
      const oldPos = oldShownIndices.indexOf(masterIdx);
      const userEdits = currentEdits[oldPos] || {};
      return { ...masterConfigsRef.current[masterIdx], ...userEdits };
    });

    setValue('maintenanceConfigs', nextConfigs);
  };

  const handleAddMaintenanceConfig = () => {
    const usedIndices = new Set(shownIndicesRef.current);
    const nextIdx = masterConfigsRef.current.findIndex((_, i) => !usedIndices.has(i));
    if (nextIdx === -1) return;
    shownIndicesRef.current = [...shownIndicesRef.current, nextIdx];
    setValue('maintenanceConfigs', [
      ...watchedMaintenanceConfigs,
      { ...masterConfigsRef.current[nextIdx] }
    ]);
  };

  const allConfigsShown = shownIndicesRef.current.length >= masterConfigsRef.current.length;

  useEffect(() => {
    // Ensure at least one photo field is present
    if (photoFields.length === 0) {
      appendPhoto(null);
    }
  }, [appendPhoto, photoFields.length]);

  return (
    <Stack gap={2} width={'100%'}>
      <Controller
        name="assetName"
        control={control}
        render={({ field, fieldState }) => (
          <InputText
            title="Asset Name"
            placeholder="Asset Name"
            inputError={fieldState.error?.message}
            inputProps={{
              ...field
            }}
          />
        )}
      />

      <Controller
        name="assetId1"
        control={control}
        render={({ field, fieldState }) => (
          <InputText
            title="Asset ID"
            placeholder="Asset ID"
            inputError={fieldState.error?.message}
            inputProps={{
              ...field
            }}
          />
        )}
      />

      <Controller
        name="assetId2"
        control={control}
        render={({ field, fieldState }) => (
          <InputText
            title="Asset ID 2 (Optional)"
            placeholder="Asset ID 2 (Optional)"
            inputError={fieldState.error?.message}
            inputProps={{
              ...field
            }}
          />
        )}
      />

      <Controller
        name="assetType"
        control={control}
        render={({ field: { onChange, value }, fieldState }) => (
          <InputSelect
            title="Asset Type"
            value={value}
            placeholder="Select Asset Type"
            inputError={fieldState?.error?.message as string}
            onChange={(e: SelectChangeEvent<unknown>) => {
              const selectedTypeId = e.target.value as string;
              onChange(selectedTypeId);

              const typeModels = compact(
                listOption?.modelsData?.filter((m) => m.type?.id === Number(selectedTypeId))
              );
              if (typeModels.length > 0) {
                const autoModel = typeModels[0];
                setValue('modelId', autoModel.id.toString());
                if (autoModel?.manufacturer?.id) {
                  setValue('manufacturer', autoModel.manufacturer.id.toString());
                } else {
                  setValue('manufacturer', '');
                }
                if (Array.isArray(autoModel?.oemAttributes)) {
                  setValue(
                    'oemAttributes',
                    autoModel.oemAttributes.map((attr) => ({
                      label: attr.label,
                      value: attr.value,
                      isDropdown: attr.isDropdown ?? false,
                      dropdownId: attr.dropdownId ?? null,
                      dropdownValue: attr.dropdownValue ?? null
                    }))
                  );
                } else {
                  setValue('oemAttributes', []);
                }

                // ADD THIS — sync maintenance configs when assetType auto-selects a model
                // GET responses carry triggerSubTypeId as a plain number — normalize to
                // a string so it matches the form field.
                const autoConfigs = (
                  (autoModel?.maintenanceConfigs || []) as MaintenanceConfig[]
                ).map((c) => ({
                  ...c,
                  triggerSubTypeId: normalizeTriggerSubTypeId(c.triggerSubTypeId)
                }));
                const autoEnabled = autoModel?.maintenanceEnabled ?? false;
                console.log(
                  'autoModel:',
                  autoModel?.name,
                  '| configs:',
                  autoConfigs,
                  '| enabled:',
                  autoEnabled
                );
                setMasterConfigs(autoConfigs, autoEnabled);
                if (autoConfigs.length > 0 && autoEnabled) {
                  shownIndicesRef.current = autoConfigs.map((_, i) => i);
                  setValue('maintenanceEnabled', true);
                  setValue(
                    'maintenanceConfigs',
                    autoConfigs.map((c) => ({ ...c }))
                  );
                } else {
                  shownIndicesRef.current = [];
                  setValue('maintenanceEnabled', false);
                  setValue('maintenanceConfigs', []);
                }
              } else {
                setValue('modelId', '');
                setValue('manufacturer', '');
                setValue('oemAttributes', []);
                setMasterConfigs([], false);
                shownIndicesRef.current = [];
                setValue('maintenanceEnabled', false);
                setValue('maintenanceConfigs', []);
              }
            }}
            options={listOption?.asset_types?.map((opt) => ({
              label: opt.name,
              value: opt.id.toString()
            }))}
          />
        )}
      />

      <Controller
        name="modelId"
        control={control}
        render={({ field: { onChange, value }, fieldState }) => {
          // Filter models by selected AssetType
          const selectedAssetTypeId = watch('assetType');
          let availableModels =
            compact(
              selectedAssetTypeId
                ? listOption?.modelsData?.filter((m) => m.type?.id === Number(selectedAssetTypeId))
                : listOption?.modelsData
            ) || [];

          // If a model is currently selected but not in the filtered list (e.g. legacy data), include it so the dropdown displays correctly
          if (value && value !== '0' && selectedAssetTypeId) {
            const isValueInAvailable = availableModels.some(
              (m) => m.id.toString() === value.toString()
            );
            if (!isValueInAvailable) {
              const currentModel = listOption?.modelsData.find(
                (m) => m.id.toString() === value.toString()
              );
              if (currentModel) {
                availableModels = [...availableModels, currentModel];
              }
            }
          }

          return (
            <InputSelect
              title="Model"
              value={value}
              placeholder="Select Model"
              inputError={fieldState?.error?.message}
              onChange={(e: SelectChangeEvent<unknown>) => {
                const v = e.target.value;
                onChange(v);
                if (v === '0') {
                  setValue('oemAttributes', []);
                  return;
                }
                const model = listOption?.modelsData.find((m) => m.id === Number(v));
                if (model?.manufacturer?.id) {
                  setValue('manufacturer', model.manufacturer.id.toString());
                }
                // Initialize OEM attributes from model
                if (Array.isArray(model?.oemAttributes)) {
                  setValue(
                    'oemAttributes',
                    model.oemAttributes.map((attr) => ({
                      label: attr.label,
                      value: attr.value,
                      isDropdown: attr.isDropdown ?? false,
                      dropdownId: attr.dropdownId ?? null,
                      dropdownValue: attr.dropdownValue ?? null
                    }))
                  );
                }
                const manualConfigs = (
                  (model?.maintenanceConfigs || []) as MaintenanceConfig[]
                ).map((c) => ({
                  ...c,
                  triggerSubTypeId: normalizeTriggerSubTypeId(c.triggerSubTypeId)
                }));
                const manualEnabled = model?.maintenanceEnabled ?? false;
                setMasterConfigs(manualConfigs, manualEnabled); // ← pass both together

                if (manualConfigs.length > 0 && manualEnabled) {
                  shownIndicesRef.current = manualConfigs.map((_, i) => i);
                  setValue('maintenanceEnabled', true);
                  setValue(
                    'maintenanceConfigs',
                    manualConfigs.map((c) => ({ ...c }))
                  );
                } else {
                  shownIndicesRef.current = [];
                  setValue('maintenanceEnabled', false);
                  setValue('maintenanceConfigs', []);
                  setValue('oemAttributes', []);
                }
              }}
              options={
                availableModels?.length > 0
                  ? [
                      { label: 'Other', value: '0' },
                      ...(availableModels?.map((m) => ({
                        label: m.name,
                        value: m.id.toString()
                      })) || [])
                    ]
                  : []
              }
            />
          );
        }}
      />

      {watch('modelId') === '0' && (
        <Controller
          name="model"
          control={control}
          render={({ field, fieldState }) => (
            <InputText
              title="Model Name"
              placeholder="Model Name"
              inputError={fieldState.error?.message}
              inputProps={{
                ...field
              }}
            />
          )}
        />
      )}

      <Controller
        name="manufacturer"
        control={control}
        render={({ field: { onChange, value }, fieldState }) => (
          <InputSelect
            title="Manufacturer"
            value={value}
            placeholder="Select Manufacturer"
            inputError={fieldState?.error?.message}
            onChange={(e: SelectChangeEvent<unknown>) => {
              onChange(e.target.value);
            }}
            options={[
              { label: 'Other', value: '0' },
              ...(listOption?.manufactures || []).map((m) => ({
                label: m.name,
                value: m.id.toString()
              }))
            ]}
          />
        )}
      />

      {watch('manufacturer') === '0' && (
        <Controller
          name="manufacturerName"
          control={control}
          render={({ field, fieldState }) => (
            <InputText
              title="Manufacturer Name"
              placeholder="Manufacturer Name"
              inputError={fieldState.error?.message}
              inputProps={{
                ...field
              }}
            />
          )}
        />
      )}

      {/* OEM Attributes Section */}
      {watch('oemAttributes')?.length > 0 && (
        <Stack gap={2} sx={{ border: '1px solid #eee', p: 2, borderRadius: 2 }}>
          <Typography fontWeight={700} color={'#226AAA'}>
            OEM Attributes
          </Typography>
          {(watch('oemAttributes') || []).map(
            (
              attr: { value: string; label: string; isDropdown: boolean; dropdownId: number },
              index: number
            ) => {
              const baseName = `oemAttributes.${index}`;

              if (attr.isDropdown && attr.dropdownId) {
                // Dropdown type OEM attribute
                const dropdownValues = getOemDropdownValues(attr.dropdownId);
                return (
                  <Stack key={index} gap={1}>
                    <Controller
                      name={`${baseName}.dropdownValue`}
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <InputSelect
                          title={attr.label}
                          value={value ? value.toString() : ''}
                          placeholder={`Select ${attr.label}`}
                          onChange={(e: SelectChangeEvent<unknown>) => {
                            const val = e.target.value;
                            onChange(val === '' ? null : Number(val));
                            // Also set displayed value
                            const selectedOption = dropdownValues.find(
                              (dv) => dv.id === Number(val)
                            );
                            if (selectedOption) {
                              setValue(
                                `${baseName}.value`,
                                selectedOption.value || selectedOption.name
                              );
                            }
                          }}
                          options={[
                            { value: '', label: 'Select...' },
                            ...dropdownValues.map((dv) => ({
                              value: dv.id.toString(),
                              label: dv.value || dv.name
                            }))
                          ]}
                        />
                      )}
                    />
                  </Stack>
                );
              }

              // Text type OEM attribute
              return (
                <Controller
                  key={index}
                  name={`${baseName}.value`}
                  control={control}
                  render={({ field }) => (
                    <InputText
                      title={attr.label}
                      placeholder={attr.label}
                      inputProps={{
                        ...field
                      }}
                    />
                  )}
                />
              );
            }
          )}
        </Stack>
      )}

      {/* ── Maintenance Schedule ── */}
      {hasAnyConfigs && (
        <Stack gap={2} sx={{ border: '1px solid #eee', p: 2, borderRadius: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!watchMaintenanceEnabled}
                onChange={(e) => handleMaintenanceToggle(e.target.checked)}
              />
            }
            label={<Typography>Enable Maintenance Schedule</Typography>}
          />

          {watchMaintenanceEnabled && (
            <Stack gap={2}>
              {watchedMaintenanceConfigs.map((config: MaintenanceConfig, index: number) => {
                const isIot = config.counterSource === 'IOT';
                return (
                  <Stack
                    key={index}
                    gap={1}
                    sx={{ border: '1px solid #D0D9E4', borderRadius: '10px', p: 2 }}>
                    {/* Trigger type — read only label */}
                    <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
                      <Typography>
                        Trigger Type :{' '}
                        {triggerTypeOptions.find(
                          (t) => t.value === String(config.maintenanceTriggerTypeId)
                        )?.label ?? config.maintenanceTriggerTypeId}
                        {config.triggerSubTypeId
                          ? ` (${
                              subTypeOptions.find((s) => s.value === config.triggerSubTypeId)
                                ?.label ?? config.triggerSubTypeId
                            })`
                          : ''}
                      </Typography>
                      <IconButton
                        onClick={() => setPendingRemoveIndex(index)}
                        disabled={watchMaintenanceEnabled && watchedMaintenanceConfigs.length <= 1}>
                        <DeleteIcon />
                      </IconButton>
                    </Stack>

                    {/* frequencyType */}
                    <Controller
                      control={control}
                      name={`maintenanceConfigs.${index}.frequencyType`}
                      render={({ field: { onChange, value }, fieldState }) => (
                        <InputSelect
                          title={'Frequency Type'}
                          value={value || ''}
                          placeholder={'Select Frequency Type'}
                          onChange={(e: SelectChangeEvent<unknown>) =>
                            onChange(e.target.value as string)
                          }
                          sx={{ padding: 0, minHeight: '46px', height: '46px' }}
                          options={[
                            { value: 'INTERVAL', label: 'Interval' },
                            { value: 'RANGE', label: 'Range' }
                          ]}
                          inputError={fieldState.error?.message}
                        />
                      )}
                    />

                    {/* INTERVAL */}
                    {watch(`maintenanceConfigs.${index}.frequencyType`) === 'INTERVAL' && (
                      <Controller
                        name={`maintenanceConfigs.${index}.maintenanceFrequency`}
                        control={control}
                        render={({ field: { onChange, value, ...rest }, fieldState }) => (
                          <NumericInput
                            title={'Maintenance Frequency'}
                            placeholder={'e.g. 500'}
                            inputError={fieldState.error?.message}
                            value={value}
                            onChange={onChange}
                            restProps={rest}
                          />
                        )}
                      />
                    )}

                    {/* RANGE */}
                    {watch(`maintenanceConfigs.${index}.frequencyType`) === 'RANGE' && (
                      <>
                        <Controller
                          name={`maintenanceConfigs.${index}.minMaintenanceFrequency`}
                          control={control}
                          render={({ field: { onChange, value, ...rest }, fieldState }) => (
                            <NumericInput
                              title={'Min Maintenance Frequency'}
                              placeholder={'e.g. 10'}
                              inputError={fieldState.error?.message}
                              value={value}
                              onChange={onChange}
                              restProps={rest}
                            />
                          )}
                        />
                        <Controller
                          name={`maintenanceConfigs.${index}.maxMaintenanceFrequency`}
                          control={control}
                          render={({ field: { onChange, value, ...rest }, fieldState }) => (
                            <NumericInput
                              title={'Max Maintenance Frequency'}
                              placeholder={'e.g. 30'}
                              inputError={fieldState.error?.message}
                              value={value}
                              onChange={onChange}
                              restProps={rest}
                            />
                          )}
                        />
                      </>
                    )}

                    {/* notificationWindow */}
                    <Controller
                      name={`maintenanceConfigs.${index}.notificationWindow`}
                      control={control}
                      render={({ field: { onChange, value, ...rest }, fieldState }) => (
                        <NumericInput
                          title={'Notification Window'}
                          placeholder={'e.g. 24 (hours before due)'}
                          value={value}
                          inputError={fieldState.error?.message}
                          onChange={onChange}
                          restProps={rest}
                        />
                      )}
                    />

                    {/* IoT fields — only visible when counterSource is 'iot' */}
                    {isIot && (
                      <>
                        <Controller
                          name={`maintenanceConfigs.${index}.iotDeviceId`}
                          control={control}
                          render={({ field, fieldState }) => (
                            <InputText
                              title={'IoT Device ID'}
                              placeholder={'Enter IoT Device ID'}
                              inputError={fieldState.error?.message}
                              inputProps={{ ...field }}
                            />
                          )}
                        />
                        <Controller
                          name={`maintenanceConfigs.${index}.iotDeviceName`}
                          control={control}
                          render={({ field, fieldState }) => (
                            <InputText
                              title={'IoT Device Name'}
                              placeholder={'Enter IoT Device Name'}
                              inputError={fieldState.error?.message}
                              inputProps={{ ...field }}
                            />
                          )}
                        />
                      </>
                    )}
                  </Stack>
                );
              })}

              {/* Add next config — only shown when not all configs are visible */}
              {!allConfigsShown && (
                <Stack width={'100%'} alignItems={'flex-end'}>
                  <ButtonCustom
                    label={'Add Maintenance Config'}
                    style={{ width: '260px' }}
                    onClick={handleAddMaintenanceConfig}
                  />
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      )}

      {assetIssueOptions?.map((opt) => {
        const fieldName = `option_${opt.id}`;
        const otherFieldName = `option_${opt.id}_name`;
        return (
          <React.Fragment key={opt.id}>
            <Controller
              name={fieldName}
              control={control}
              render={({ field, fieldState }) => (
                <InputSelect
                  title={capitalizeFirstLetter(opt.name.replaceAll('_', ' '))}
                  value={field.value}
                  placeholder={`Select ${capitalizeFirstLetter(opt.name.replaceAll('_', ' '))}`}
                  inputError={fieldState.error?.message}
                  onChange={(e: SelectChangeEvent<unknown>) => {
                    const val = e.target.value;
                    field.onChange(val);

                    // Reset Action By if choosing OTHER
                    if (val === 'OTHER') {
                      setValue('actionBy', '');
                    }
                  }}
                  options={opt.dropdownValues.map((dv) => ({
                    label: dv.value,
                    value: dv.value
                  }))}
                />
              )}
            />

            {/* OTHER */}
            {watch(fieldName) === 'OTHER' && (
              <Controller
                name={otherFieldName}
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <InputText
                    title={`${capitalizeFirstLetter(opt.name.replaceAll('_', ' '))} Name`}
                    placeholder={`${capitalizeFirstLetter(opt.name.replaceAll('_', ' '))} Name`}
                    inputError={fieldState.error ? 'This field is required' : ''}
                    inputProps={{
                      ...field,
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      onChange: (e: any) => {
                        field.onChange(e.target.value);
                        setValue('actionBy', '');
                      }
                    }}
                  />
                )}
              />
            )}
          </React.Fragment>
        );
      })}
      {assetIssueField?.map((field) => (
        <React.Fragment key={field.id}>
          {field.type === 'DATE' ? (
            <Controller
              name={`single_${field.id}`}
              control={control}
              render={({ field: f }) => (
                <DatePicker
                  title={`${field.name}:`}
                  onChange={(val) =>
                    setValue(`single_${field.id}`, val ? dayjs(val).toISOString() : '')
                  }
                  value={f.value ? dayjs(f.value) : undefined}
                />
              )}
            />
          ) : (
            <Controller
              name={`single_${field.id}`}
              control={control}
              render={({ field: f, fieldState }) => (
                <InputText
                  title={`${field.name}:`}
                  placeholder={`Enter ${field.name}`}
                  inputProps={{ ...f }}
                  inputError={fieldState.error?.message}
                />
              )}
            />
          )}
        </React.Fragment>
      ))}
      {/* <Controller name="criticality" ... legacy 1–5 select /> */}
      <Controller
        name="criticalityKey"
        control={control}
        render={({ field: { onChange, value }, fieldState }) => (
          <InputSelect
            title="Criticality"
            value={value || ''}
            placeholder="Select Criticality"
            inputError={fieldState?.error?.message}
            onChange={(e: SelectChangeEvent<unknown>) => {
              onChange(e.target.value);
            }}
            options={criticalityKeyOptions}
          />
        )}
      />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Controller
          name="lastMaintenance"
          control={control}
          render={({ field, fieldState }) => (
            <DatePicker
              inputError={fieldState.error?.message}
              title={`Last Maintenance`}
              onChange={(val) => setValue(`lastMaintenance`, val ? dayjs(val).toISOString() : '')}
              value={field.value ? dayjs(field.value) : undefined}
            />
          )}
        />
      </LocalizationProvider>
      <Controller
        name="note"
        control={control}
        render={({ field, fieldState }) => (
          <InputText
            title="Note"
            placeholder="Note"
            inputError={fieldState.error?.message}
            inputProps={{
              ...field
            }}
          />
        )}
      />

      <Controller
        name="otherNote"
        control={control}
        render={({ field, fieldState }) => (
          <InputText
            title="Other Note"
            placeholder="Other Note"
            inputError={fieldState.error?.message}
            inputProps={{
              ...field,
              multiline: true,
              minRows: 3
            }}
          />
        )}
      />
      {/*
      <AddPhoto
        errorText={errors?.assetEntry?.photos?.message || ''}
        label={'Asset Photo(s):'}
        value={watch('photos')}
        onChange={(file) => {
          if (file) {
            setValue('photos', file);
          }
        }}
      />
      */}
      {/* Dynamic Fields by Category (scaffold) */}
      <Stack gap={2} alignItems={'flex-end'}>
        {photoFields.map((item, index) => (
          <AddPhoto
            key={item.id}
            label={`Asset Photo ${index + 1}`}
            value={watch(`photos.${index}`)}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            errorText={(errors?.photos as any)?.[index]?.message}
            onChange={(file) => {
              if (file) {
                setValue(`photos.${index}`, file);
              }
            }}
            onRemove={() => removePhoto(index)}
          />
        ))}
        <ButtonCustom
          style={{ width: 'fit-content' }}
          label="Add More Photo"
          onClick={() => appendPhoto(null)}
        />
      </Stack>
      {assetDynamic.keys.map((cat) => (
        <Stack key={cat} gap={2} sx={{ border: '1px solid #eee', p: 2, borderRadius: 2 }}>
          <p>{capitalizeFirstLetter(cat.replaceAll('_', ' '))}</p>
          {assetDynamic.entities[cat].map(
            /* eslint-disable @typescript-eslint/no-explicit-any */
            (item: any) => {
              const index = item.index;
              const fieldDef = item.field;
              const baseName = `entries.${index}`;
              const dropdownOptionId = fieldDef?.dropdownValueOption?.id?.toString();
              const dropdownOption = dropdownOptionMap.get(dropdownOptionId);
              if (dropdownOption) {
                return (
                  <React.Fragment key={index}>
                    <Controller
                      name={`${baseName}.dropdownValueId`}
                      control={control}
                      render={({ field }) => (
                        <InputSelect
                          title={`${fieldDef.name} ${fieldDef.unit ? `(${fieldDef.unit})` : ''}`}
                          value={field.value}
                          placeholder={`Select ${fieldDef.name}`}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            setValue(`${baseName}.value`, '');
                          }}
                          options={dropdownOption.dropdownValues.map((dv: any) => ({
                            label: dv.value,
                            value: dv.id
                          }))}
                        />
                      )}
                    />

                    {/* OTHER */}
                    {watch(`${baseName}.dropdownValueId`) &&
                      dropdownOption.dropdownValues.find(
                        (dv: any) => dv.id === watch(`${baseName}.dropdownValueId`)
                      )?.value === 'OTHER' && (
                        <Controller
                          name={`${baseName}.dropdownOtherValue`}
                          control={control}
                          render={({ field }) => (
                            <InputText
                              title={`${fieldDef.name} Other`}
                              placeholder="Enter value"
                              inputProps={field}
                            />
                          )}
                        />
                      )}
                  </React.Fragment>
                );
              }

              if (fieldDef.type === 'DATE') {
                return (
                  <Controller
                    key={index}
                    name={`${baseName}.value`}
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        title={`${fieldDef.name} ${fieldDef.unit ? `(${fieldDef.unit})` : ''}`}
                        value={field.value ? dayjs(field.value) : undefined}
                        onChange={(val) => field.onChange(val ? dayjs(val).toISOString() : '')}
                      />
                    )}
                  />
                );
              }

              /** ================= DEFAULT INPUT ================= */
              return (
                <Controller
                  key={index}
                  name={`${baseName}.value`}
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputText
                      title={`${fieldDef.name} ${fieldDef.unit ? `(${fieldDef.unit})` : ''}`}
                      placeholder={fieldDef.name}
                      inputError={fieldState.error?.message}
                      inputProps={field}
                    />
                  )}
                />
              );
            }
          )}
        </Stack>
      ))}

      <DialogConfirm
        isOpen={pendingRemoveIndex !== null}
        onClose={() => setPendingRemoveIndex(null)}
        textTitle={'Remove Maintenance Config'}
        textButton={'Remove'}
        textCancel={'Cancel'}
        isDelete
        isIconClose
        textContent={'Are you sure you want to remove this maintenance config?'}
        onSubmit={() => {
          if (pendingRemoveIndex !== null) {
            if (watchedMaintenanceConfigs.length <= 1) {
              setPendingRemoveIndex(null);
              return;
            }
            handleRemoveMaintenanceConfig(pendingRemoveIndex);
            setPendingRemoveIndex(null);
          }
        }}
      />
    </Stack>
  );
};
